// VISTA EM CARDS da página de Monitoramento Hidrológico.
//
// Herda o vocabulário visual do portal do cliente (grupos que fluem na
// horizontal, card clicável, farol de silêncio), com três diferenças que
// existem porque aqui quem olha é a GESTÃO da Hydrodata, não o cliente:
//
//  1. Um grupo "Precisa de ação" no topo, que ATRAVESSA clientes e SOME quando
//     está vazio. A tela só gasta pixel quando existe decisão a tomar — card de
//     "0 problemas" é decoração.
//  2. O valor da estação muda NÃO some (no portal ele some, pra o cliente não
//     ler número velho como se fosse atual). Aqui é o contrário: ver o último
//     valor é como se distingue sensor travado de sensor mudo.
//  3. Rodapé de pendências — só o que falta, e só onde faz sentido faltar.
import React from 'react';
import { Badge, Card, Empty, Tag, Tooltip, Typography } from 'antd';
import type { Estacao } from './shared';
import {
  ESTADO_COR, ESTADO_ROTULO, avaliarSilencio, estadoDe, idadeTexto,
  pendenciasDe, textoCadencia,
} from './situacao';

const { Text } = Typography;

const nf = (v: number, casas = 2) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

const ROTULO_TIPO: Record<string, string> = {
  fluviometrica: 'Fluviométrica', pluviometrica: 'Pluviométrica',
  linimetrica: 'Linimétrica', outro: 'Outro',
};
const ROTULO_FORMA: Record<string, string> = {
  convencional: 'régua', automatica: 'automática', telemetrica: 'telemétrica',
};

export interface Grupo { chave: string; titulo: string; sub?: string; itens: Estacao[]; esmaecido?: boolean }

// Agrupa em três níveis, do mais urgente ao mais arquivado. O grupo de ação
// atravessa clientes de propósito: quem abre a tela de manhã quer ver o que
// quebrou, não navegar cliente por cliente pra descobrir.
export function agrupar(estacoes: Estacao[], por: 'cliente' | 'contrato' | 'nenhum', agoraMs = Date.now()): Grupo[] {
  const precisaAcao = estacoes.filter(e => ['sem_dado', 'silenciosa', 'modem_fora'].includes(estadoDe(e, agoraMs)));
  const idsAcao = new Set(precisaAcao.map(e => e.id));
  const resto = estacoes.filter(e => !idsAcao.has(e.id));
  const inativas = resto.filter(e => e.status === 'inativa');
  const ativas = resto.filter(e => e.status !== 'inativa');

  const grupos: Grupo[] = [];
  if (precisaAcao.length) {
    grupos.push({ chave: '_acao', titulo: 'Precisa de ação', itens: precisaAcao,
                  sub: 'muda, sem dado nenhum, ou com o modem fora' });
  }
  if (por === 'nenhum') {
    if (ativas.length) grupos.push({ chave: '_ativas', titulo: 'Em operação', itens: ativas });
  } else {
    const mapa = new Map<string, Grupo>();
    for (const e of ativas) {
      const chave = por === 'cliente'
        ? (e.cliente_nome || '(sem cliente)')
        : (e.contrato_numero || '(sem contrato)');
      const sub = por === 'cliente' ? undefined : (e.contrato_titulo || undefined);
      if (!mapa.has(chave)) mapa.set(chave, { chave, titulo: chave, sub, itens: [] });
      mapa.get(chave)!.itens.push(e);
    }
    // Ordem ESTÁVEL (nº de estações desc, depois nome): a lista se recarrega
    // sozinha a cada minuto, e ordenar por gravidade faria os grupos trocarem
    // de lugar debaixo do cursor no meio de uma conferência.
    grupos.push(...Array.from(mapa.values()).sort((a, b) =>
      b.itens.length - a.itens.length || a.titulo.localeCompare(b.titulo, 'pt-BR')));
  }
  if (inativas.length) {
    grupos.push({ chave: '_inativas', titulo: 'Inativas', itens: inativas, esmaecido: true,
                  sub: 'desligadas de propósito — seguem consultáveis' });
  }
  return grupos;
}

function CardEstacao({ e, aoAbrir, mostrarCliente, agoraMs }: {
  e: Estacao; aoAbrir: (e: Estacao) => void; mostrarCliente: boolean; agoraMs: number;
}) {
  const estado = estadoDe(e, agoraMs);
  const sil = avaliarSilencio(e, agoraMs);
  const pend = pendenciasDe(e);
  const tipos = e.tipos?.length ? e.tipos : (e.tipo ? [e.tipo] : []);
  const formas = e.formas?.length ? e.formas : ['convencional'];

  // A manchete é o último dado, venha de onde vier. Se as duas origens existem,
  // ganha a mais recente — é ela que responde "a estação está viva?".
  const temTele = e.ultimo_valor_tele != null && e.ultima_telemetria_em;
  const temMan = e.ultimo_valor_manual != null && e.ultima_leitura_em;
  const teleMaisNova = temTele && (!temMan
    || +new Date(e.ultima_telemetria_em!) >= +new Date(e.ultima_leitura_em!));
  const manchete = teleMaisNova && temTele
    ? { valor: e.ultimo_valor_tele!, unidade: e.unidade || 'm', em: e.ultima_telemetria_em!, origem: 'telemetria' }
    : temMan
      ? { valor: e.ultimo_valor_manual!, unidade: e.ultimo_valor_unidade || e.unidade || 'm', em: e.ultima_leitura_em!, origem: 'régua' }
      : null;

  return (
    <Card
      size="small" role="button" tabIndex={0}
      aria-label={`Abrir estação ${e.nome}`}
      onClick={() => aoAbrir(e)}
      onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); aoAbrir(e); } }}
      onMouseEnter={(ev) => { ev.currentTarget.style.borderColor = '#0930D0'; }}
      onMouseLeave={(ev) => { ev.currentTarget.style.borderColor = '#e8e8e8'; }}
      styles={{ body: { padding: 0 } }}
      style={{ height: '100%', cursor: 'pointer', borderColor: '#e8e8e8',
               transition: 'border-color .15s', opacity: e.status === 'inativa' ? 0.72 : 1 }}>
      <div style={{ display: 'flex', height: '100%' }}>
        {/* Barra de estado: é o que deixa varrer 14 cards sem ler texto. */}
        <div style={{ width: 3, background: ESTADO_COR[estado], flex: '0 0 3px' }} />
        <div style={{ padding: 10, minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <Text strong style={{ fontSize: 13, minWidth: 0, flex: 1 }} ellipsis={{ tooltip: e.nome }}>{e.nome}</Text>
            {estado !== 'ok' && (
              <Tag color={estado === 'inativa' ? 'default' : 'warning'}
                   style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px' }}>
                {ESTADO_ROTULO[estado]}
              </Tag>
            )}
          </div>
          <div style={{ fontSize: 10.5, letterSpacing: '0.04em', color: '#8c8c8c', marginTop: 1 }}>
            {e.codigo}{mostrarCliente && e.cliente_nome ? ` · ${e.cliente_nome}` : ''}
          </div>
          {e.corpo_hidrico && (
            <div style={{ fontSize: 11, color: '#595959', marginTop: 2 }}>{e.corpo_hidrico}</div>
          )}

          <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0 6px' }} />

          {manchete ? (
            <div>
              <span style={{ fontSize: 22, fontWeight: 600, color: '#0930D0', lineHeight: 1.1 }}>
                {nf(manchete.valor, Math.abs(manchete.valor) < 10 ? 2 : 1)}
              </span>
              <span style={{ fontSize: 12, color: '#595959', marginLeft: 4 }}>{manchete.unidade}</span>
              <div style={{ fontSize: 11, color: sil.silenciosa ? '#B07500' : '#8c8c8c', marginTop: 1 }}>
                {idadeTexto(manchete.em, agoraMs)} · {manchete.origem}
                {sil.silenciosa && ' · sem transmitir'}
              </div>
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>nenhuma leitura registrada</Text>
          )}

          {/* Linha de operação — só existe no ERP. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', marginTop: 8, fontSize: 11, color: '#595959' }}>
            <span>{tipos.map(t => ROTULO_TIPO[t] || t).join(' + ') || '—'}</span>
            <span style={{ color: '#8c8c8c' }}>{formas.map(f => ROTULO_FORMA[f] || f).join(' + ')}</span>
            {e.cadencia_seg ? <span>a cada {textoCadencia(e.cadencia_seg)}</span> : null}
            {(e.variaveis_n || 0) > 1 ? <span>{e.variaveis_n} variáveis</span> : null}
            {e.modem_conectado != null && (
              <Tooltip title={e.modem_conectado
                ? `Conectado ao IoT Core desde ${e.modem_mudou_em ? new Date(e.modem_mudou_em).toLocaleString('pt-BR') : '?'}`
                : 'Desconectado do IoT Core'}>
                <Badge status={e.modem_conectado ? 'success' : 'error'}
                       text={<span style={{ fontSize: 11 }}>modem</span>} />
              </Tooltip>
            )}
          </div>

          {/* Registros: os dois números EMPILHADOS, nunca somados. "0 régua ·
              949.807 telemetria" conta que a automática funciona; "0 · 0" da
              F04-AUT denuncia que ela nunca mediu. Somar apagaria a distinção. */}
          <div style={{ fontSize: 10.5, color: '#8c8c8c', marginTop: 3 }}>
            {(e.leituras_total || 0).toLocaleString('pt-BR')} régua · {(e.telemetria_total || 0).toLocaleString('pt-BR')} telemetria
          </div>

          {pend.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
              {pend.map(p => (
                <Tooltip key={p.chave} title={p.dica}>
                  <Tag style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px',
                                background: '#fffbe6', borderColor: '#ffe58f', color: '#7a5b00' }}>
                    {p.rotulo}
                  </Tag>
                </Tooltip>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export function CardsEstacoes({ estacoes, agruparPor, aoAbrir, agoraMs = Date.now() }: {
  estacoes: Estacao[];
  agruparPor: 'cliente' | 'contrato' | 'nenhum';
  aoAbrir: (e: Estacao) => void;
  agoraMs?: number;
}) {
  const grupos = agrupar(estacoes, agruparPor, agoraMs);
  if (!grupos.length) return <Empty description="Nenhuma estação neste filtro" style={{ marginTop: 40 }} />;

  return (
    // Grupos FLUEM na horizontal: um cliente com 1 estação divide a linha com
    // outro em vez de gastar a largura inteira (é o que o portal faz e o que
    // faz caber 5 clientes sem rolagem).
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px 28px', alignItems: 'flex-start' }}>
      {grupos.map(g => (
        <div key={g.chave} style={{ flex: '0 1 auto', maxWidth: '100%', minWidth: 'min(290px, 100%)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8,
                        paddingBottom: 5, borderBottom: '1px solid #f0f0f0' }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em',
                           textTransform: 'uppercase', whiteSpace: 'nowrap',
                           color: g.chave === '_acao' ? '#B07500' : (g.esmaecido ? '#bfbfbf' : '#020D7F') }}>
              {g.titulo}
            </span>
            {g.sub && (
              <Text type="secondary" style={{ fontSize: 11, minWidth: 0, maxWidth: 260 }} ellipsis={{ tooltip: g.sub }}>
                {g.sub}
              </Text>
            )}
            <span style={{ flex: 1, minWidth: 8 }} />
            <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{g.itens.length}</Text>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'stretch' }}>
            {g.itens.map(e => (
              <div key={e.id} style={{ width: 'clamp(240px, 100%, 290px)', flex: '1 1 240px' }}>
                <CardEstacao e={e} aoAbrir={aoAbrir} agoraMs={agoraMs}
                  mostrarCliente={agruparPor !== 'cliente' || g.chave === '_acao' || g.chave === '_inativas'} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
